import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Plane,
  Stamp,
  Ticket,
  BookUser,
  ShieldCheck
} from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const ServicesSection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();
  const trackRef = useRef<HTMLDivElement>(null);

  const services = [
    {
      icon: Plane,
      title: 'Tours',
      description: 'Curated journeys & handcrafted itineraries.',
      color: 'text-primary-deep-blue',
      bgColor: 'bg-primary-deep-blue bg-opacity-10',
    },
    {
      icon: Stamp,
      title: 'Visa',
      description: 'End-to-end visa processing & guidance.',
      color: 'text-primary-royal-purple',
      bgColor: 'bg-primary-royal-purple bg-opacity-10',
    },
    {
      icon: Ticket,
      title: 'Air Ticket Booking',
      description: 'Best fares across global airlines.',
      color: 'text-primary-emerald',
      bgColor: 'bg-primary-emerald bg-opacity-10',
    },
    {
      icon: BookUser,
      title: 'Passport Assistance',
      description: 'Hassle-free application & renewals.',
      color: 'text-accent-copper',
      bgColor: 'bg-accent-copper bg-opacity-10',
    },
    {
      icon: ShieldCheck,
      title: 'Travel Insurance',
      description: 'Peace of mind for every trip.',
      color: 'text-primary-gold',
      bgColor: 'bg-primary-gold bg-opacity-10',
    },
  ];

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !isVisible) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isScrollable = () => track.scrollWidth > track.clientWidth + 4;
    if (prefersReducedMotion || !isScrollable()) return;

    let index = 0;
    let paused = false;

    const pause = () => { paused = true; };
    const resume = () => { paused = false; };

    const interval = window.setInterval(() => {
      if (paused || !isScrollable()) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const step = track.scrollWidth / track.children.length;
      index += 1;
      let left = index * step;
      if (left >= maxScroll - 2) {
        left = 0;
        index = 0;
      }
      track.scrollTo({ left, behavior: 'smooth' });
    }, 2200);

    track.addEventListener('pointerdown', pause);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('mouseenter', pause);
    track.addEventListener('mouseleave', resume);
    track.addEventListener('touchend', resume, { passive: true });

    return () => {
      window.clearInterval(interval);
      track.removeEventListener('pointerdown', pause);
      track.removeEventListener('touchstart', pause);
      track.removeEventListener('mouseenter', pause);
      track.removeEventListener('mouseleave', resume);
      track.removeEventListener('touchend', resume);
    };
  }, [isVisible]);

  return (
    <section ref={ref} className="pt-2xl md:pt-3xl pb-lg bg-neutral-white">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="relative overflow-hidden rounded-3xl border border-neutral-medium-gray gradient-gallery-page shadow-lg px-6 py-10 md:px-12 md:py-14">
            {/* Decorative accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 gradient-accent" />

            <div className="text-center mb-8 md:mb-10">
              <h2 className="heading-secondary text-neutral-charcoal mb-2">
                Everything You Need, All in One Place
              </h2>
              <p className="body-regular text-secondary-slate max-w-2xl mx-auto">
                From dream destinations to the finest details, we handle every part of your travel journey.
              </p>
            </div>

            <div ref={trackRef} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4 -mx-2 px-2 lg:grid lg:grid-cols-5 lg:gap-6 lg:mx-0 lg:px-0 lg:overflow-visible">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  className="group snap-start shrink-0 w-[42%] sm:w-[30%] lg:w-auto flex flex-col items-center text-center p-4 md:p-6 rounded-2xl bg-white bg-opacity-70 backdrop-blur border border-white hover:bg-opacity-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 ${service.bgColor} rounded-xl mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon size={24} className={`md:w-6 md:h-6 ${service.color}`} />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-neutral-charcoal mb-1 group-hover:text-primary-deep-blue transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-xs text-secondary-slate leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
